/**
 * API Service - Configuración de Axios
 * =====================================
 *
 * Configuración segura:
 * - Usa httpOnly cookies para autenticación (NO localStorage)
 * - withCredentials: true para enviar/recibir cookies
 * - Interceptor para manejar errores 401 (sesión expirada)
 */

import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // CRÍTICO: Enviar cookies httpOnly automáticamente
  withCredentials: true,
});

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirigir a login (la cookie httpOnly se limpia en el servidor)
      // Solo redirigir si no estamos ya en login o páginas públicas
      const publicPaths = ['/', '/login', '/forgot-password', '/reset-password', '/activate', '/privacy', '/terms', '/propuesta', '/firmar'];
      const currentPath = window.location.pathname;

      if (!publicPaths.some(p => currentPath.startsWith(p) || currentPath === p)) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// APIs específicas
export const empresasAPI = {
  listar: () => api.get('/empresas'),
  obtener: (id) => api.get(`/empresas/${id}`),
};

export const asignacionesAPI = {
  crear: (data) => api.post('/asignaciones', data),
  actualizar: (id, data) => api.put(`/asignaciones/${id}`, data),
  listar: (params) => api.get('/asignaciones', { params }),
  buscar: (matricula, fecha) => api.get(`/asignaciones/buscar?matricula=${matricula}&fecha=${fecha}`),
  obtener: (id) => api.get(`/asignaciones/${id}`),
  importar: (formData) => api.post('/asignaciones/importar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  descargarPlantilla: () => api.get('/asignaciones/plantilla', {
    responseType: 'blob'
  }),
};

export const multasAPI = {
  listar: (params) => api.get('/multas', { params }),
  obtener: (id) => api.get(`/multas/${id}`),
};

export const subarrendatariosAPI = {
  listar: (params) => api.get('/admin/subarrendatarios', { params }),
  obtener: (id) => api.get(`/admin/subarrendatarios/${id}`),
  crear: (data) => api.post('/admin/subarrendatarios', data),
  actualizar: (id, data) => api.put(`/admin/subarrendatarios/${id}`, data),
  desactivar: (id) => api.patch(`/admin/subarrendatarios/${id}/desactivar`),
  activar: (id) => api.patch(`/admin/subarrendatarios/${id}/activar`),
  eliminar: (id) => api.delete(`/admin/subarrendatarios/${id}`),
  buscarPorFecha: (matricula, fecha) => api.get('/admin/subarrendatarios/buscar/fecha', { params: { matricula, fecha } }),
};

export const asignacionesSubarrendatariosAPI = {
  listar: (params) => api.get('/admin/asignaciones-subarrendatarios', { params }),
  obtener: (id) => api.get(`/admin/asignaciones-subarrendatarios/${id}`),
  crear: (data) => api.post('/admin/asignaciones-subarrendatarios', data),
  actualizar: (id, data) => api.put(`/admin/asignaciones-subarrendatarios/${id}`, data),
  finalizar: (id, fecha_fin) => api.patch(`/admin/asignaciones-subarrendatarios/${id}/finalizar`, { fecha_fin }),
  desactivar: (id) => api.patch(`/admin/asignaciones-subarrendatarios/${id}/desactivar`),
  eliminar: (id) => api.delete(`/admin/asignaciones-subarrendatarios/${id}`),
  historial: (matricula) => api.get(`/admin/asignaciones-subarrendatarios/historial/${matricula}`),
};

export const firmasAPI = {
  obtenerEstado: (asignacionId) => api.get(`/firmas/asignacion/${asignacionId}`),
  solicitarEntrega: (asignacionId) => api.post(`/firmas/solicitar-entrega/${asignacionId}`),
  solicitarDevolucion: (asignacionId) => api.post(`/firmas/solicitar-devolucion/${asignacionId}`),
  validarToken: (token) => api.get(`/firmas/validar-token/${token}`),
  registrar: (data) => api.post('/firmas/registrar', data),
  registrarPresencial: (asignacionId, data) => api.post(`/firmas/presencial/${asignacionId}`, data),
  descargarDocumento: (firmaId) => api.get(`/firmas/documento/${firmaId}`, { responseType: 'blob' }),
  verificar: (firmaId) => api.get(`/firmas/verificar/${firmaId}`),
  obtenerClausulas: (tipoFirma) => api.get(`/firmas/clausulas/${tipoFirma}`),
};

export const facturacionAPI = {
  historico: () => api.get('/admin/facturacion/historico'),
  detalleMes: (mes, anio) => api.get('/admin/facturacion/detalle', { params: { mes, anio } }),
  descargarExcel: (mes, anio) => api.post('/admin/reportes/generar', { año: anio, mes }, { responseType: 'blob' }),
  enviarEmail: (mes, anio) => api.post('/admin/reportes/enviar', { año: anio, mes }),
};

export default api;
